import { useAcquireBookForm } from "../hooks/useAcquireBookForm";
import type { AcquireMultipleBookHandoffData } from "@/types/acquirebook";
import type {
  BookRequestCheckboxStatus,
  BookRequestCheckboxStatuses,
} from "@/features/acquireMultipleBooks/types";
import { useApiClient } from "@/hooks/useApiClient";
import React, { useEffect, useState } from "react";
import { useLocation, Link as RouterLink, Navigate } from "react-router-dom";
import type { IPrefecture } from "@/types/prefectures";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { PropertySelectionRow } from "@/features/map/types";
import type { ChibanInput } from "@/types/kaokuNumber";
import { GetPrefecturesAPI } from "@/api";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { AlertBar } from "@/features/acquireMultipleBooks/components/AlertBar";
import {
  Box,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Modal,
  Typography,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { PageTitle } from "@/components/Title";
import Grid from "@mui/material/Grid";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import { Button, ButtonVariantOption } from "@/components/Button";
import { FeedGrid } from "@/features/acquireMultipleBooks/components/FeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { ChibanKaokuNumberSearchModal } from "@/components/KaokuNumberSearch/ChibanKaokuNumberSearchModal";
import { Label } from "@/components/Label";
import {
  isArrayOfAcquireMultipleBookHandoffData,
  isObjectWithAcquireMultipleBookLocationState,
} from "@/utils/utilsAcquireBook";
import { useUserConfig } from "@/hooks/useUserConfig";
import { ConfirmAcquireMultipleBooksDialog } from "../components/ConfirmAcquireMultipleBooksDialog";
import {
  AllMattersAnalyzeOptionsCheckbox,
  type AllMattersAnalyzeOptions,
} from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";
import {
  PAST_DAYS_MIN,
  PAST_DAYS_MAX,
  PAST_DAYS_MIN_MESSAGE,
  PAST_DAYS_MAX_MESSAGE,
  PAST_DAYS_TYPE_MESSAGE,
} from "@/utils/pastDaysValidation";

export const DISTINCT_MESSAGE =
  "重複項目が見つかったため、自動で取り除きました";

/** 中央寄せのFlexBox用スタイル（解析オプション周りのBoxで再利用） */
const centerFlexBoxSx = {
  mb: 1,
  display: "flex" as const,
  justifyContent: "center" as const,
};

// チェックボックスの初期状態を設定するための値を生成する
export const makeBookRequestCheckboxStatus = (
  checked: boolean,
  disabled: boolean
): BookRequestCheckboxStatus => {
  return { checked, disabled };
};

// チェックボックスの状態を管理するstateの初期値を生成する
export const makeOptionsInitialState = (
  rows: AcquireMultipleBookHandoffData[]
): BookRequestCheckboxStatuses[] => {
  // 土地の場合と建物の場合とでチェックボックスを表示するか非表示にするか判定する関数

  const checkDisable = (type: string, bookType: string): boolean => {
    // false: 表示, true: 非表示
    const table: Record<string, Record<string, boolean>> = {
      kyoutan: { LAND: false, BUILDING: false, CONDOMINIUM: false },
      sintaku: { LAND: false, BUILDING: false, CONDOMINIUM: false },
      genzai: { LAND: false, BUILDING: false, CONDOMINIUM: false },
      requestClosureBook: { LAND: false, BUILDING: false, CONDOMINIUM: false },
      ownerInfo: { LAND: false, BUILDING: false, CONDOMINIUM: false },
      electricDrawings: { LAND: false, BUILDING: true, CONDOMINIUM: true },
      tisekiDrawings: { LAND: false, BUILDING: true, CONDOMINIUM: true },
      tiekiDrawings: { LAND: false, BUILDING: true, CONDOMINIUM: true },
      buildingDrawings: { LAND: true, BUILDING: false, CONDOMINIUM: false },
    };
    return table[type][bookType] ?? false;
  };

  return rows.map((value) => {
    return {
      // ここでチェックボックスの初期状態を設定する
      kyoutan: makeBookRequestCheckboxStatus(
        false,
        checkDisable("kyoutan", value.bookType)
      ),
      sintaku: makeBookRequestCheckboxStatus(
        false,
        checkDisable("sintaku", value.bookType)
      ),
      genzai: makeBookRequestCheckboxStatus(
        false,
        checkDisable("genzai", value.bookType)
      ),
      requestClosureBook: makeBookRequestCheckboxStatus(
        false,
        checkDisable("requestClosureBook", value.bookType)
      ),
      ownerInfo: makeBookRequestCheckboxStatus(
        false,
        checkDisable("ownerInfo", value.bookType)
      ),
      electricDrawings: makeBookRequestCheckboxStatus(
        false,
        checkDisable("electricDrawings", value.bookType)
      ),
      tisekiDrawings: makeBookRequestCheckboxStatus(
        false,
        checkDisable("tisekiDrawings", value.bookType)
      ),
      tiekiDrawings: makeBookRequestCheckboxStatus(
        false,
        checkDisable("tiekiDrawings", value.bookType)
      ),
      buildingDrawings: makeBookRequestCheckboxStatus(
        false,
        checkDisable("buildingDrawings", value.bookType)
      ),
    };
  });
};

// 重複を取り除く
export const distinct = (
  rows: AcquireMultipleBookHandoffData[]
): AcquireMultipleBookHandoffData[] => {
  // 作業用の空配列を作る
  const workingArray: Array<{
    key: string;
    value: AcquireMultipleBookHandoffData;
  }> = [];

  rows.forEach((value) => {
    // 同一項目と識別するためのkeyを生成する
    const key = `${value.bookType}${value.prefName}${value.locationFull}${value.chibanKaokuNumber}`;

    // 作業用配列から上で生成したkeyと同じkeyを持っている項目があるか探す
    const anItemThatHasSameKey = workingArray.find((item) => item.key === key);

    if (anItemThatHasSameKey === undefined) {
      // 同じkeyを持っていないならそのkeyで作業用配列に新規追加
      workingArray.push({ key, value });
    }
    // 同じkeyを持っているならそのvalueは捨てる
  });

  // 最終的に出来上がった作業用配列からvalueのみを取り出す
  return workingArray.map((item, index): AcquireMultipleBookHandoffData => {
    // idを採番し直す
    item.value.id = index + 1;
    return item.value;
  });
};

export const AcquireMultipleBooks: React.FC = () => {
  const { handleSubmit } = useAcquireBookForm();
  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();

  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = React.useState(false);
  const [withOwnerAnalyze, setWithOwnerAnalyze] = React.useState(false);
  const [reuseOnlyOwn, setReuseOnlyOwn] = React.useState(false);
  const [withAllMattersAnalyze, setWithAllMattersAnalyze] =
    React.useState(false);
  const [allMattersAnalyzeOptions, setAllMattersAnalyzeOptions] =
    React.useState<AllMattersAnalyzeOptions>({
      includeTitleSection: true,
      includeKoku: true,
      includeOtsuku: true,
      analyzeMethodType: "rule_based",
    });

  // 解析オプション未選択時のエラーメッセージ（送信ブロック時のみ表示）
  const [allMattersOptionsError, setAllMattersOptionsError] = React.useState<
    string | null
  >(null);

  // 「家屋番号検索」ボタンの活性非活性制御State
  const [
    kaokuNumberSearchButtonIsDisabled,
    setKaokuNumberSearchButtonIsDisabled,
  ] = React.useState<boolean>(true);

  // 「家屋番号検索」モーダル表示非表示管理State
  const [kaokuNumberSearchModalIsOpen, setKaokuNumberSearchModalIsOpen] =
    React.useState<boolean>(false);

  // 遷移元の画面からデータを引き継ぐ
  const location = useLocation();

  let rows: AcquireMultipleBookHandoffData[] = [];
  let initialIdentifyName = "";
  let initialPastDays: number | undefined;

  // 型が複数あるので、以降で型ガードを使って判定する
  // TODO AcquireMultipleBookLocationStateの型に統一したい
  if (isObjectWithAcquireMultipleBookLocationState(location.state)) {
    // location.stateがオブジェクトで、data, identifyName, pastDaysが存在する場合
    rows = location.state.data;
    initialIdentifyName = location.state.identifyName;
    initialPastDays = location.state.pastDays;
  } else if (isArrayOfAcquireMultipleBookHandoffData(location.state)) {
    // AcquireMultipleBookHandoffData[] の場合
    rows = location.state;
    // この場合、identifyNameとpastDaysはデフォルト値のままになる
  } else {
    // nullまたはその他の場合
    rows = [];
  }

  // 重複を取り除く
  const [distinctedRows, setDistinctedRows] = useState<
    AcquireMultipleBookHandoffData[]
  >(distinct(rows));

  // 重複があった場合に表示するメッセージを設定する
  const [message, setMessage] = useState(
    distinctedRows.length !== rows.length ? DISTINCT_MESSAGE : ""
  );

  // チェックボックスの状態を管理するstateを生成する
  const [checkBoxOptions, setCheckBoxOptions] = useState(
    makeOptionsInitialState(distinctedRows)
  );

  // 都道府県リストのState
  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);

  const [disableConfirmModalCancelButton, setDisableConfirmModalCancelButton] =
    useState<boolean>(false);

  const isOwnerInfoChecked = (
    checkBoxOptions: BookRequestCheckboxStatuses[]
  ): boolean => {
    for (const option of checkBoxOptions) {
      if (option.ownerInfo.checked) {
        return true;
      }
    }
    return false;
  };

  const isAllMattersChecked = (
    checkBoxOptions: BookRequestCheckboxStatuses[]
  ): boolean => {
    for (const option of checkBoxOptions) {
      if (
        option.kyoutan.checked ||
        option.sintaku.checked ||
        option.genzai.checked ||
        option.requestClosureBook.checked
      ) {
        return true;
      }
    }
    return false;
  };

  const { ownerInfoViaScraping, mypageTop, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();

  // Zodスキーマ定義
  const FormDataSchema = z.object({
    identifyName: z.string().max(256, "識別名は256文字以内です"),
    pastDays: z
      .number({ invalid_type_error: PAST_DAYS_TYPE_MESSAGE })
      .min(PAST_DAYS_MIN, PAST_DAYS_MIN_MESSAGE)
      .max(PAST_DAYS_MAX, PAST_DAYS_MAX_MESSAGE),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type FormData = z.infer<typeof FormDataSchema>;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<FormData>({
    mode: "all",
    defaultValues: {
      identifyName: initialIdentifyName,
      pastDays: initialPastDays,
    },
    resolver: zodResolver(FormDataSchema),
  });

  // ユーザー設定取得
  useUserConfig({
    onUserSettingsLoaded: (data) => {
      // 0だと条件falseになるので明示的にundefinedで比較
      if (initialPastDays === undefined) {
        // 初期値が設定されていない場合は、APIから取得した値をセットする
        reactHookForm.setValue("pastDays", data.pictureRequestPastDays);
      }
    },
    // 一度でも入力があったら更新しないようにする
    enableCallbackOnRefetch: !reactHookForm.formState.isDirty,
  });

  /**
   * 「prefName」が一致するprefectureのprefCodeを取得する。
   * @param {string} prefName
   * @returns {string}
   */
  const getPrefCode = (prefName: string): string => {
    const targetPrefecture = prefectures.find(
      (element) => element.name === prefName
    );

    return targetPrefecture != null ? targetPrefecture.prefCode : "";
  };

  /**
   * 物件リストに物件を追加する処理。
   * @param {PropertySelectionRow[]} selectRows
   */
  const appendPropertySelectionRows = (
    selectRows: PropertySelectionRow[]
  ): void => {
    // distinctedRowsのidの最大値を取得。
    const lastElement = distinctedRows.at(-1);
    let maxId = lastElement != null ? lastElement.id : 0;

    // AcquireMultipleBookHandoffData型に変換。
    const convertRows = selectRows.map((selectedRow) => {
      const element: AcquireMultipleBookHandoffData = {
        id: ++maxId, // IDはdistinctedRowsの連番にする。
        bookType: selectedRow.bookType,
        prefCode: getPrefCode(selectedRow.prefName),
        prefName: selectedRow.prefName,
        locationFull: selectedRow.location,
        chibanKaokuNumber: selectedRow.chibanKaokuNumber,
        kyoutan: false,
        sintaku: false,
        genzai: false,
        ownerInfo: false,
        electricDrawings: false,
        tisekiDrawings: false,
        tiekiDrawings: false,
        buildingDrawings: false,
      };

      return element;
    });

    // 変換した配列とリストに表示されている配列をマージして重複削除。
    const mergeDistinctRows = distinct(distinctedRows.concat(convertRows));

    // 重複削除した配列のチェックボックスを作成。
    if (distinctedRows.length < mergeDistinctRows.length) {
      // マージした配列の方が大きければ、追加した分のチェックボックスを用意。
      const addedRows = mergeDistinctRows.filter(
        (_, index) => distinctedRows[index] === undefined
      );

      if (addedRows.length > 0)
        setCheckBoxOptions(
          checkBoxOptions.concat(makeOptionsInitialState(addedRows))
        );
    }

    setDistinctedRows(mergeDistinctRows);
  };

  /**
   * チェックが入っているデータを取得し、
   * ChibanKaokuNumberSearchModal の chibanList に一致する型に変換する。
   * @returns {ChibanInput[]}
   */
  const convertChibanList = (): ChibanInput[] => {
    return distinctedRows
      .filter(
        (_, index) =>
          checkBoxOptions[index].buildingDrawings.checked ||
          checkBoxOptions[index].electricDrawings.checked ||
          checkBoxOptions[index].genzai.checked ||
          checkBoxOptions[index].kyoutan.checked ||
          checkBoxOptions[index].requestClosureBook.checked ||
          checkBoxOptions[index].ownerInfo.checked ||
          checkBoxOptions[index].sintaku.checked ||
          checkBoxOptions[index].tiekiDrawings.checked ||
          checkBoxOptions[index].tisekiDrawings.checked
      )
      .map((row) => {
        return {
          prefectureName: row.prefName,
          location: row.locationFull,
          chiban: row.chibanKaokuNumber,
          bookType: row.bookType,
        };
      });
  };

  useEffect(() => {
    // いずれかでチェックがあれば活性化
    const hasAnyChecked = (indices: number[]): boolean => {
      return indices.some((i) => {
        const option = checkBoxOptions[i];
        return (
          option.buildingDrawings.checked ||
          option.electricDrawings.checked ||
          option.genzai.checked ||
          option.kyoutan.checked ||
          option.requestClosureBook.checked ||
          option.ownerInfo.checked ||
          option.sintaku.checked ||
          option.tiekiDrawings.checked ||
          option.tisekiDrawings.checked
        );
      });
    };

    const allIndices = distinctedRows.map((_, i) => i);
    const hasAnyCheckedItems = hasAnyChecked(allIndices);
    setKaokuNumberSearchButtonIsDisabled(!hasAnyCheckedItems);
  }, [checkBoxOptions, distinctedRows]);

  useEffect(() => {
    // 種別が「閉鎖」の登記種別にチェックが入っていたら「共担」「信託」以外のチェックを非活性にする。
    // LANDとそれ以外でチェックボックスの状態は違うので場合分けして制御する
    const updatedCheckBoxOptions = checkBoxOptions.map((option, index) => {
      if (distinctedRows[index].bookType === "LAND") {
        if (option.requestClosureBook.checked) {
          return {
            ...option,
            genzai: { checked: false, disabled: true },
            ownerInfo: { checked: false, disabled: true },
            electricDrawings: { checked: false, disabled: true },
            tisekiDrawings: { checked: false, disabled: true },
            tiekiDrawings: { checked: false, disabled: true },
          };
        } else {
          return {
            ...option,
            genzai: { ...option.genzai, disabled: false },
            ownerInfo: { ...option.ownerInfo, disabled: false },
            electricDrawings: {
              ...option.electricDrawings,
              disabled: false,
            },
            tisekiDrawings: {
              ...option.tisekiDrawings,
              disabled: false,
            },
            tiekiDrawings: {
              ...option.tiekiDrawings,
              disabled: false,
            },
          };
        }
      } else {
        if (option.requestClosureBook.checked) {
          return {
            ...option,
            genzai: { checked: false, disabled: true },
            ownerInfo: { checked: false, disabled: true },
            buildingDrawings: { checked: false, disabled: true },
          };
        } else {
          return {
            ...option,
            genzai: { ...option.genzai, disabled: false },
            ownerInfo: { ...option.ownerInfo, disabled: false },
            buildingDrawings: { ...option.buildingDrawings, disabled: false },
          };
        }
      }
    });
    setCheckBoxOptions(updatedCheckBoxOptions);
  }, [checkBoxOptions]);

  useEffect(() => {
    (async () => {
      // 都道府県リストをAPIから取得する
      const prefectures = await GetPrefecturesAPI(apiClient);
      if (prefectures.length !== 0) setPrefectures(prefectures);
    })();
  }, []);

  useEffect(() => {
    // 該当のチェックボックスが全てチェックされていない場合、解析をfalseに変更する
    const ownerCheckList = checkBoxOptions.map(
      (option) => option.ownerInfo.checked
    );
    if (ownerCheckList.every((value) => !value)) {
      setWithOwnerAnalyze(false);
    }
    const allMattersCheckList = checkBoxOptions.map(
      (option) =>
        option.kyoutan.checked ||
        option.sintaku.checked ||
        option.genzai.checked ||
        option.requestClosureBook.checked
    );
    if (allMattersCheckList.every((value) => !value)) {
      setWithAllMattersAnalyze(false);
    }
  }, [checkBoxOptions]);

  if (rows.length === 0) {
    return <Navigate replace to="/acquiremultiplebooks/upload" />;
  }

  return (
    <PagePaper>
      {message !== "" ? (
        <AlertBar
          message={message}
          onClose={() => {
            setMessage("");
          }}
        />
      ) : (
        ""
      )}
      <form>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <RequestQuoteIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>不動産登記簿の一括取得</PageTitle>
          </Box>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ ml: 0, color: "red", flexShrink: 0 }}>
              <ul>
                <li>
                  所在および地番・家屋番号は登記簿記載の内容を正しく入力しなければエラーとなり取得できないケースがございます。詳細はこちらの
                  <Link
                    href="https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd#1d114754f2df810f8650c8420f7074f3"
                    target="_blank"
                  >
                    マニュアル
                  </Link>
                  をご確認ください
                  <br />
                </li>
              </ul>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6} mb={1}>
            <Grid container spacing={1} justifyContent={"center"}>
              <Grid item xs={6}>
                <Label>識別名</Label>
                <CTextFieldIdentifyName
                  name={"identifyName"}
                  control={reactHookForm.control}
                  size={SizingWrapperStyle.INHERIT}
                />
              </Grid>
              <Grid item xs={6}>
                <Label required={true}>経過日数</Label>

                <CTextFieldPastDays
                  name="pastDays"
                  control={reactHookForm.control}
                  size={SizingWrapperStyle.INHERIT}
                  disabled={reuseOnlyOwn}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={1} sx={{ mt: 5 }}>
            <Button
              disabled={kaokuNumberSearchButtonIsDisabled}
              label="地番・家屋番号検索"
              onClick={() => {
                setKaokuNumberSearchModalIsOpen(true);
              }}
              size={SizingWrapperStyle.MEDIUM}
              variant={ButtonVariantOption.Outlined}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <MuiCheckbox
                checked={reuseOnlyOwn}
                onChange={(e) => {
                  setReuseOnlyOwn(e.target.checked);
                }}
              />
            }
            label="取得済不動産登記のみ取得する（初めて取得する地番・家屋番号が含まれる場合はこちらのチェックは外してください）"
          />
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            列名をダブルクリックすると全選択する
          </Typography>
        </Box>
        <SectionPaper>
          <FeedGrid
            rows={distinctedRows}
            checkBoxOptions={checkBoxOptions}
            setCheckBoxOptions={setCheckBoxOptions}
          />
          {ownerInfoViaScraping ? (
            <Box sx={{ ...centerFlexBoxSx, mt: 6 }}>
              <FormControlLabel
                control={
                  <MuiCheckbox
                    sx={{ padding: "0px 4px" }}
                    checked={withOwnerAnalyze}
                    disabled={!isOwnerInfoChecked(checkBoxOptions)}
                    onChange={(e) => {
                      setWithOwnerAnalyze(e.target.checked);
                    }}
                  />
                }
                label="所有者事項の取得と同時に内容を解析する"
              />
            </Box>
          ) : null}
          {displayCheckboxAllMattersAnalyze ? (
            <>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <MuiCheckbox
                      sx={{ padding: "0px 4px" }}
                      checked={withAllMattersAnalyze}
                      disabled={!isAllMattersChecked(checkBoxOptions)}
                      onChange={(e) => {
                        setWithAllMattersAnalyze(e.target.checked);
                      }}
                    />
                  }
                  label="全部事項の取得と同時に内容を解析する"
                />
              </Box>
              {withAllMattersAnalyze && (
                <Box
                  sx={{
                    ...centerFlexBoxSx,
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <AllMattersAnalyzeOptionsCheckbox
                    disabled={!isAllMattersChecked(checkBoxOptions)}
                    value={allMattersAnalyzeOptions}
                    onChange={(value) => {
                      setAllMattersAnalyzeOptions(value);
                      setAllMattersOptionsError(null);
                    }}
                  />
                  {allMattersOptionsError != null && (
                    <FormHelperText error sx={{ textAlign: "center" }}>
                      {allMattersOptionsError}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </>
          ) : null}
          <Box sx={{ ...centerFlexBoxSx, mt: 1, mb: 2 }}>
            <Button
              variant={ButtonVariantOption.Contained}
              onClick={() => {
                void (async () => {
                  // 現在がチェックかつ信託か共担のどちらかがチェックされていなかった場合、メッセージを表示する
                  const isInvalidOption = checkBoxOptions.some((value) => {
                    if (
                      value.genzai.checked &&
                      !(value.sintaku.checked || value.kyoutan.checked)
                    ) {
                      alert(
                        "現在事項を取得する場合には共担か信託を選択してください"
                      );
                      return true;
                    }
                    return false;
                  });
                  if (isInvalidOption) return;
                  // 一つもチェックがない場合、メッセージを表示する
                  const result = checkBoxOptions.some((value) => {
                    return (
                      Object.keys(value) as Array<
                        keyof BookRequestCheckboxStatuses
                      >
                    ).some((key) => value[key].checked);
                  });
                  if (!result) {
                    alert("取得したい登記種別を最低一つはチェックしてください");
                    return;
                  }

                  // 全部事項解析ONかつ解析オプションが1つも選択されていない場合はエラー表示
                  if (
                    withAllMattersAnalyze &&
                    !allMattersAnalyzeOptions.includeTitleSection &&
                    !allMattersAnalyzeOptions.includeKoku &&
                    !allMattersAnalyzeOptions.includeOtsuku
                  ) {
                    setAllMattersOptionsError(
                      "解析オプションは最低1つ選択してください"
                    );
                    return;
                  }
                  setAllMattersOptionsError(null);

                  // 一つでもチェックがあれば通す。チェックされていない行は取得登録されない
                  await reactHookForm.handleSubmit((_data, _event) => {
                    setOpenModalFlag(true);
                  })();
                })();
              }}
              label={"一括取得する"}
            />
          </Box>
        </SectionPaper>

        {/* 確認のモーダル画面 */}
        <ConfirmAcquireMultipleBooksDialog
          open={openModalFlag}
          setOpen={setOpenModalFlag}
          checkBoxOptions={checkBoxOptions}
          handleAcquireButtonClick={(_event, cancelLoading) => {
            handleSubmit(
              distinctedRows,
              checkBoxOptions,
              reactHookForm.getValues("pastDays"),
              withOwnerAnalyze,
              withAllMattersAnalyze,
              reuseOnlyOwn,
              reactHookForm.getValues("identifyName"),
              cancelLoading,
              setDisableConfirmModalCancelButton,
              allMattersAnalyzeOptions
            );
          }}
          disableConfirmModalCancelButton={disableConfirmModalCancelButton}
          isReadOnly={isReadOnly}
        />

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>

      {/* 家屋番号検索モーダル */}
      <Modal
        open={kaokuNumberSearchModalIsOpen}
        aria-labelledby={"kaoku-number-search-modal"}
        aria-describedby={"kaoku-number-search-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ChibanKaokuNumberSearchModal
              variant={"book"}
              chibanList={convertChibanList()}
              setKaokuNumberSearchModalIsOpen={setKaokuNumberSearchModalIsOpen}
              appendPropertySelectionRows={appendPropertySelectionRows}
            />
          </Box>
        </Box>
      </Modal>
    </PagePaper>
  );
};
